import queryString from 'query-string';
import { geocodeLocation, getPlacePredictions } from '../utils/googleMapsApi';
import { getAddressFields } from '../utils/geolocation';
import { getIsPostcodeSearch } from '../selectors/locationSelectors';

import {
  UPDATE_LOCATION_DETAILS,
  RESTAURANT_LOCATION_SEARCH,
  BRAND_LOCATION_SEARCH,
  RECENT_LOCATIONS,
  LOCATION_SEARCH_MODAL_SHOW,
  LOCATION_SEARCH_MODAL_HIDE,
  CLEAR_LOCATION_QUERY,
  CHANGE_HIGHLIGHTED_SUGGESTION,
  GOOGLE_MAPS_GEOLOCATION_REQUEST,
  GOOGLE_MAPS_GEOLOCATION_SUCCESS,
  GOOGLE_MAPS_GEOLOCATION_FAILURE,
  GOOGLE_MAPS_SUGGESTIONS_REQUEST,
  GOOGLE_MAPS_SUGGESTIONS_SUCCESS,
  GOOGLE_MAPS_SUGGESTIONS_FAILURE,
  GOOGLE_MAPS_CLEAR_SUGGESTIONS,
  GOOGLE_MAPS_SELECT_SUGGESTION_SUCCESS,
  GOOGLE_MAPS_SELECT_SUGGESTION_FAILURE,
  GOOGLE_MAPS_GEOCODE_REQUEST,
  GOOGLE_MAPS_GEOCODE_SUCCESS,
  GOOGLE_MAPS_GEOCODE_FAILURE,
  SHOW_UNDELIVERABLE_MODAL,
  HIDE_UNDELIVERABLE_MODAL,
  FOCUS_LOCATION_SEARCH,
  BLUR_LOCATION_SEARCH,
} from './types';

import { trackTappedLocationPicker, trackAddressRefine } from './tracking/location';

const MINIMUM_LOCATION_ACCURACY = 50;

export function updateLocationDetails(location) {
  const { post_code, formatted_address, lat, lng } = location; // eslint-disable-line camelcase
  return {
    type: UPDATE_LOCATION_DETAILS,
    payload: {
      post_code,
      formatted_address,
      lat,
      lng,
    },
  };
}

export function locationRestaurantSearch({ location, isSavedAddress, filter }) {
  return (dispatch, getState) => {
    const {
      request,
      homepageFeaturedContent: { filter: homepageFilter },
    } = getState();

    const { tags, menu_group_uname } = filter || homepageFilter || {}; // eslint-disable-line camelcase

    return dispatch({
      type: RESTAURANT_LOCATION_SEARCH,
      host: request.host,
      endpoint: '/api/restaurants',
      options: {
        credentials: 'same-origin',
        method: 'POST',
        body: {
          saved: isSavedAddress,
          location,
          tags,
          menu_group_uname,
        },
      },
    });
  };
}

export function locationBrandSearch({ location, brand }) {
  return (dispatch, getState) => {
    const { request } = getState();
    const { post_code, geohash } = location; // eslint-disable-line camelcase

    const query = queryString.stringify({
      uname: brand,
      post_code,
      geohash,
    });

    return dispatch({
      type: BRAND_LOCATION_SEARCH,
      host: request.host,
      endpoint: `/orderapp/v1/brands/search?${query}`,
    });
  };
}

export function focusLocationSearch() {
  return {
    type: FOCUS_LOCATION_SEARCH,
  };
}

export function blurLocationSearch() {
  return {
    type: BLUR_LOCATION_SEARCH,
  };
}

export function navigateToSavedAddress({ id, searchType = 'restaurants', filter, brand }) {
  return (dispatch, getState) => {
    const address = getState().location.recentAddresses.locations.find(
      (location) => location.id === id,
    );

    const location = {
      id: address.id,
      formatted_address: address.formattedAddress,
      post_code: address.postcode,
      place_id: address.placeId,
      coordinates: address.coordinates,
    };

    switch (searchType) {
      case 'restaurants':
        return locationRestaurantSearch({ location, isSavedAddress: true, filter })(
          dispatch,
          getState,
        );
      case 'brand':
        return locationBrandSearch({ location, brand })(dispatch, getState);
      default:
        return Promise.reject();
    }
  };
}

export function requestRecentAddresses() {
  return (dispatch, getState) => {
    const { country, unverifiedUserId, apiAuth } = getState().request;
    if (apiAuth) {
      return dispatch({
        type: RECENT_LOCATIONS,
        endpoint: `/orderapp/v1/users/${unverifiedUserId}/addresses?sort=recent&tld=${country.tld}`,
      });
    }
    return Promise.resolve();
  };
}

export function showLocationSearchModal() {
  return (dispatch) => {
    dispatch({
      type: LOCATION_SEARCH_MODAL_SHOW,
    });
    dispatch(trackTappedLocationPicker('restaurant list'));
  };
}

export function hideLocationSearchModal() {
  return {
    type: LOCATION_SEARCH_MODAL_HIDE,
  };
}

export function clearLocationQuery() {
  return {
    type: CLEAR_LOCATION_QUERY,
  };
}

export function changeHighlightedSuggestion({ newSuggestion }) {
  return {
    type: CHANGE_HIGHLIGHTED_SUGGESTION,
    payload: {
      highlightedSuggestion: newSuggestion,
    },
  };
}

export function locationFromGeolocation({ position, source = 'geolocation', sourceView }) {
  return (dispatch, getState) => {
    const state = getState();
    const { googleApiKey } = state.request;
    const isPostcodeSearch = getIsPostcodeSearch(state);
    const coords = { lat: position.coords.latitude, lng: position.coords.longitude };

    dispatch({
      type: GOOGLE_MAPS_GEOLOCATION_REQUEST,
    });

    const dispatchGeolocationSuccess = ([place]) => {
      const { formattedAddress, placeId, postalCode } = getAddressFields({ place });
      const isLocationAccurate =
        source === 'map' || position.coords.accuracy < MINIMUM_LOCATION_ACCURACY;

      trackAddressRefine({
        action: 'VIEWED',
        sourceView,
        ...coords,
        searchString: formattedAddress,
        label: 'onload',
      });

      dispatch({
        type: GOOGLE_MAPS_GEOLOCATION_SUCCESS,
        payload: {
          formattedAddress: isPostcodeSearch ? postalCode : formattedAddress,
          placeId,
          coords,
          postalCode,
          isLocationAccurate,
        },
      });
    };

    const dispatchGeolocationFailed = (status) =>
      dispatch({
        type: GOOGLE_MAPS_GEOLOCATION_FAILURE,
        payload: {
          status,
        },
      });

    return geocodeLocation({ coords, googleApiKey })
      .then(dispatchGeolocationSuccess)
      .catch(dispatchGeolocationFailed);
  };
}

export function fetchAddressSuggestions({ searchString }) {
  return (dispatch, getState) => {
    const {
      googleApiKey,
      country: { tld },
    } = getState().request;

    if (searchString === '') return undefined;

    dispatch({
      type: GOOGLE_MAPS_SUGGESTIONS_REQUEST,
    });

    const dispatchSuggestionsFailure = (status) =>
      dispatch({
        type: GOOGLE_MAPS_SUGGESTIONS_FAILURE,
        payload: { status },
      });

    const mapAddressFields = ({
      place_id: placeId,
      structured_formatting: address,
      ...otherProps
    }) => ({
      placeId,
      address: address.main_text,
      locality: address.secondary_text,
      formattedAddress: otherProps.description,
      otherProps,
    });

    const dispatchSuggestionsSuccess = (suggestions) =>
      dispatch({
        type: GOOGLE_MAPS_SUGGESTIONS_SUCCESS,
        payload: {
          suggestions: suggestions.map(mapAddressFields),
        },
      });

    return getPlacePredictions({ searchString, tld, googleApiKey })
      .then(dispatchSuggestionsSuccess)
      .catch(dispatchSuggestionsFailure);
  };
}

export function clearAddressSuggestions() {
  return {
    type: GOOGLE_MAPS_CLEAR_SUGGESTIONS,
  };
}

export function selectAddressSuggestion({ placeId }) {
  return (dispatch, getState) => {
    const {
      request: { googleApiKey },
    } = getState();

    const dispatchGeocodeSuggestionSuccess = ([place]) => {
      const addressFields = getAddressFields({ place });

      dispatch({
        type: GOOGLE_MAPS_SELECT_SUGGESTION_SUCCESS,
        payload: {
          selectedSuggestion: addressFields,
        },
      });
    };

    const dispatchGeocodeSuggestionFailure = (error) => {
      dispatch({
        type: GOOGLE_MAPS_SELECT_SUGGESTION_FAILURE,
        payload: { error },
      });
    };

    return geocodeLocation({ placeId, googleApiKey })
      .then(dispatchGeocodeSuggestionSuccess)
      .catch(dispatchGeocodeSuggestionFailure);
  };
}

export function geocodeAddressFromString({ searchString }) {
  return (dispatch, getState) => {
    const {
      request: { googleApiKey },
    } = getState();

    dispatch({
      type: GOOGLE_MAPS_GEOCODE_REQUEST,
    });

    const dispatchGeocodeAddressSuccess = ([place]) => {
      const addressFields = getAddressFields({ place });

      dispatch({
        type: GOOGLE_MAPS_GEOCODE_SUCCESS,
        payload: {
          addressFields,
        },
      });
    };

    const dispatchGeocodeAddressFailure = (error) => {
      dispatch({
        type: GOOGLE_MAPS_GEOCODE_FAILURE,
        payload: { error },
      });
    };

    return geocodeLocation({ searchString, googleApiKey })
      .then(dispatchGeocodeAddressSuccess)
      .catch(dispatchGeocodeAddressFailure);
  };
}

export function showUndeliverableModal() {
  return {
    type: SHOW_UNDELIVERABLE_MODAL,
  };
}

export function hideUndeliverableModal() {
  return {
    type: HIDE_UNDELIVERABLE_MODAL,
  };
}
